import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useContext } from 'react';
import ToastContext from '../contexts/ToastContext';
import { supabase } from '../supabase';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const passwordRef = useRef();
  const confirmPasswordRef = useRef();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);
  const [recoveryTokenHash, setRecoveryTokenHash] = useState(null);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  useEffect(() => {
    let mounted = true;
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = searchParams.get('token');
    const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
    const type = searchParams.get('type') || hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    async function checkRecoveryLink() {
      try {
        if (!supabase) throw new Error('Supabase is not configured');

        // Supabase recovery links normally carry the session in the URL hash.
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (sessionError) throw sessionError;
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        if (tokenHash && type === 'recovery') setRecoveryTokenHash(tokenHash);
        if (!session && !token && !(tokenHash && type === 'recovery')) {
          setError('Token invalide ou manquant');
        }
      } catch (err) {
        if (mounted) setError(err?.message || 'Lien de réinitialisation invalide ou expiré');
      } finally {
        if (mounted) setCheckingLink(false);
      }
    }

    checkRecoveryLink();
    return () => { mounted = false; };
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!passwordRef.current.value || !confirmPasswordRef.current.value) {
      setError('Tous les champs sont requis');
      return;
    }

    if (passwordRef.current.value.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (passwordRef.current.value !== confirmPasswordRef.current.value) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      if (!supabase) throw new Error('Supabase is not configured');

      if (recoveryTokenHash) {
        const { error: otpError } = await supabase.auth.verifyOtp({
          type: 'recovery',
          token_hash: recoveryTokenHash
        });
        if (otpError) throw otpError;
      }

      const { error: passwordError } = await supabase.auth.updateUser({
        password: passwordRef.current.value
      });
      if (passwordError) throw passwordError;

      setSuccess(true);
      if (toast && toast.success) toast.success('Mot de passe réinitialisé avec succès');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const friendly = err?.message || 'Erreur inconnue';
      setError(friendly);
      if (toast && toast.error) toast.error(friendly);
    }
    setLoading(false);
  }

  if (checkingLink) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
          <h2>Vérification du lien...</h2>
          <p style={{color: '#666'}}>Nous vérifions votre lien de réinitialisation.</p>
        </div>
      </div>
    );
  }

  if (error && !recoveryTokenHash) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
          <h2>Lien invalide</h2>
          <div style={{
            background: 'rgba(239,68,68,0.04)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <p style={{color: '#b91c1c', marginBottom: '0'}}>
              Le lien de réinitialisation est invalide ou a expiré.
            </p>
          </div>
          <Link to="/forgot-password" className="btn btn-primary" style={{width: '100%', textAlign: 'center', textDecoration: 'none'}}>
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
          <h2>Succès</h2>
          <div style={{
            background: 'rgba(76, 175, 80, 0.04)',
            border: '1px solid #4CAF50',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <p style={{color: '#2e7d32', marginBottom: '0'}}>
              ✓ Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la connexion...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
        <h2>Réinitialiser le mot de passe</h2>

        {error && (
          <div role="alert" className="card headerless" style={{background:'rgba(239,68,68,0.04)',borderLeft:'4px solid var(--danger)',marginBottom:'12px'}}>
            <div style={{color:'var(--danger)'}}>{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="muted">Nouveau mot de passe</label>
            <input 
              className="input" 
              type="password" 
              ref={passwordRef} 
              required 
              aria-required="true" 
              placeholder="Min. 6 caractères"
              minLength="6"
            />
          </div>

          <div className="field">
            <label className="muted">Confirmer le mot de passe</label>
            <input 
              className="input" 
              type="password" 
              ref={confirmPasswordRef} 
              required 
              aria-required="true" 
              placeholder="Confirmez votre mot de passe"
              minLength="6"
            />
          </div>

          <div style={{display:'flex',justifyContent:'flex-end',marginTop:20, gap: '10px'}}>
            <Link to="/login" style={{color:'var(--primary)',textDecoration:'none',fontSize:'0.9rem', padding: '10px'}}>
              Retour à la connexion
            </Link>
            <button className="btn btn-primary" disabled={loading} type="submit">
              {loading ? 'Réinitialisation...' : 'Réinitialiser'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
